import { useEffect, useId, useState } from 'react';
import type { RaeditorStore } from '@reyka/openpolotno';
import {
  apiBase,
  withApiKey,
  buildPresetFromElements,
  captureSelectionPreview,
  filterTextElements,
  getSelectedElements,
  type CanvasElementLike,
} from '../selectionTemplates';

type Props = {
  open: boolean;
  onClose: () => void;
  onStatus: (message: string) => void;
  store: RaeditorStore;
  /** Pre-filtered text elements; if omitted, read from store selection. */
  elements?: CanvasElementLike[];
};

export function SaveTextTemplateModal({ open, onClose, onStatus, store, elements }: Props) {
  const titleId = useId();
  const [title, setTitle] = useState('My text style');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!open) return;
    setTitle('My text style');
    setSaving(false);
    setError(null);
    const texts = elements ?? filterTextElements(getSelectedElements(store));
    setCount(texts.length);
  }, [open, store, elements]);

  if (!open) return null;

  const handleSave = async () => {
    const texts = elements ?? filterTextElements(getSelectedElements(store));
    if (texts.length === 0) {
      setError('No text elements selected');
      onStatus('Save text template: select at least one text element');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      onStatus('Saving text template…');
      const preset = buildPresetFromElements(texts);
      const previewDataUrl = await captureSelectionPreview(store);
      const res = await fetch(withApiKey(`${apiBase()}/api/text-templates`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: title.trim() || 'My text style',
          json: preset,
          previewDataUrl,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as {
        uniqueId?: string;
        id?: string;
        error?: string;
        message?: string;
      };
      if (!res.ok) {
        const msg = data.message || data.error || String(res.status);
        setError(msg);
        onStatus(`Save text template failed: ${msg}`);
        return;
      }
      const id = data.uniqueId || data.id || 'ok';
      onStatus(
        `Text template saved “${title.trim() || 'My text style'}” (${id}) — reopen Text panel to see it`,
      );
      onClose();
    } catch (err) {
      console.error(err);
      setError('Save failed — is the API running?');
      onStatus('Save text template failed — is the API running?');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal__header">
          <h2 id={titleId} className="modal__title">
            Save text template
          </h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p className="modal__lead">
          Save selected text ({count} node{count === 1 ? '' : 's'}) as a Text panel preset. Relative
          positions are preserved. Reopen the Text panel after saving to refresh the list.
        </p>

        <label className="modal__label" htmlFor="save-text-tmpl-title">
          Title
        </label>
        <input
          id="save-text-tmpl-title"
          className="modal__input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="My text style"
          autoFocus
        />

        {error && (
          <div className="modal__error" role="alert">
            {error}
          </div>
        )}

        <div className="modal__actions">
          <button type="button" className="btn btn--ghost-dark" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--accent"
            disabled={saving || count === 0 || !title.trim()}
            onClick={() => void handleSave()}
          >
            {saving ? 'Saving…' : 'Save text template'}
          </button>
        </div>
      </div>
    </div>
  );
}
